import React, { useEffect, useMemo, useState } from "react";
import { ShareIntroducerTick } from "@/components/hub/ShareIntroducerTick";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDay } from "@/lib/time";
import { gbp } from "@/lib/stages";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Btn, CellMain, CellSub, Empty, Money, SearchBox, St, Tag } from "@/components/portal/crm/kit";
import { RoutePicks } from "@/components/hub/RoutePicks";

/**
 * Route a case — the whole job in one dialog.
 *
 * Owner, verbatim: *"on route deal when i press it, im very confused it does not give me routing
 * options?"* He was right: the button named after the desk's core daily task ran
 * `nav("/hub/leads")` and abandoned him on a book of every case, because the real desk picker only
 * existed three clicks away inside a single case's Routing tab.
 *
 * Two steps, no navigating: pick a case that needs a desk, then tick the desks that actually fit
 * it. The same `/hub/leads/{id}` payload the Routing tab uses supplies the matches, their fit
 * scores and their capacity, so there is one matching rule in the system, not two.
 */

const NEEDS = {
  new: { label: "Not sent yet", tone: "ink" },
  declined: { label: "Needs a new home", tone: "red" },
};

export function RouteCaseDialog({ open, onOpenChange, onRouted }) {
  const nav = useNavigate();
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sel, setSel] = useState([]);
  const [sending, setSending] = useState(false);
  const [share, setShare] = useState([]);

  useEffect(() => {
    if (!open) { setPicked(null); setDetail(null); setSel([]); setShare([]); setQ(""); return; }
    (async () => {
      setCases(null);
      try {
        const r = await api.get("/hub/leads", { params: { limit: 300 } });
        setCases((r.data.items || []).filter((l) => {
          if (l.review_status === "pending") return false;
          const live = (l.routes || []).filter((x) => x.status !== "declined");
          if (live.length) return false;              // already sitting with somebody
          return l.stage === "new" || l.stage === "declined";
        }));
      } catch { setCases([]); }
    })();
  }, [open]);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    const rows = cases || [];
    if (!n) return rows;
    return rows.filter((l) => [l.business_name, l.applicant_name, l.finance_type, l.reference]
      .filter(Boolean).join(" ").toLowerCase().includes(n));
  }, [cases, q]);

  const pick = async (lead) => {
    setPicked(lead); setDetail(null); setSel([]);
    try { const r = await api.get(`/hub/leads/${lead.id}`); setDetail(r.data); }
    catch { toast.error("Couldn't read the panel for that case"); setPicked(null); }
  };

  const options = (detail?.matched || []).filter((m) => !m.already_sent);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/hub/leads/${picked.id}/route`, { lender_ids: sel, notes: null,
        share_introducer_ids: share.filter((x) => sel.includes(x)) });
      toast.success(`${picked.business_name || picked.applicant_name} sent to ${sel.length} desk${sel.length === 1 ? "" : "s"}`);
      (r.data?.warnings || []).forEach((w) => toast.warning(w, { duration: 7000 }));
      onOpenChange(false);
      onRouted?.();
      nav(`/hub/leads/${picked.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="route-case-dialog">
        <DialogHeader>
          <DialogTitle>{picked ? "Which desks?" : "Route a case"}</DialogTitle>
          <DialogDescription>
            {picked
              ? `${picked.business_name || picked.applicant_name} · ${picked.finance_type} · ${gbp(picked.amount_gbp)} — matched on finance type and deal band, then fit-scored.`
              : "Cases waiting for a desk: never sent, or every desk passed and it needs a new home."}
          </DialogDescription>
        </DialogHeader>

        {!picked ? (
          <div className="cxp">
            <div style={{ marginBottom: 14 }}>
              <SearchBox value={q} onChange={setQ} placeholder="Search the cases waiting for a desk" testId="route-case-search" />
            </div>
            {cases === null ? (
              <Empty testId="route-case-loading">Finding the cases that need a desk…</Empty>
            ) : shown.length === 0 ? (
              <Empty testId="route-case-none">
                {(cases || []).length === 0
                  ? "Every case is already with a desk. Nothing to route."
                  : "Nothing matches that search."}
              </Empty>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 420, overflowY: "auto" }}>
                {shown.map((l) => {
                  const need = NEEDS[l.stage] || NEEDS.new;
                  const passed = (l.routes || []).filter((r) => r.status === "declined").length;
                  return (
                    <button key={l.id} className="opt" onClick={() => pick(l)}
                            data-testid={`route-case-pick-${l.id}`}
                            style={{ display: "flex", alignItems: "center", gap: 14, textAlign: "left", cursor: "pointer", width: "100%" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <CellMain>{l.business_name || l.applicant_name}</CellMain>
                        <CellSub>
                          {l.finance_type}
                          {l.reference ? ` · ${l.reference}` : ""}
                          {` · in ${ukDay(l.created_at, "—")}`}
                          {passed ? ` · ${passed} desk${passed === 1 ? "" : "s"} passed` : ""}
                        </CellSub>
                      </div>
                      <St tone={need.tone}>{need.label}</St>
                      <Money v={l.amount_gbp} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="cxp">
            {detail === null ? (
              <Empty testId="route-desks-loading">Reading the panel…</Empty>
            ) : options.length === 0 ? (
              <Empty testId="route-desks-none">
                No desk on the panel matches this one — widen a desk&apos;s criteria or add a new desk.
              </Empty>
            ) : (
              <>
              <RoutePicks options={options} selected={sel} onChange={setSel} testPrefix="route-case-picks" />
              <div style={{ display: "flex", flexDirection: "column", gap: 9, maxHeight: 360, overflowY: "auto" }}>
                {options.map((m) => (
                  <label key={m.id} className={`opt${sel.includes(m.id) ? " pick" : ""}`}
                         style={{ display: "flex", alignItems: "flex-start", gap: 13, cursor: "pointer" }}
                         data-testid={`route-desk-option-${m.id}`}>
                    <input type="checkbox" checked={sel.includes(m.id)}
                           onChange={() => setSel((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])}
                           style={{ accentColor: "var(--accent)", width: 15, height: 15, marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CellMain>{m.company_name}</CellMain>
                      <CellSub>
                        {m.company_type} · {m.commission_type === "flat"
                          ? `${gbp(m.commission_flat_gbp)} flat` : `${m.commission_pct}% commission`}
                        {m.turnaround_days != null ? ` · ${m.turnaround_days}d to a decision` : ""}
                      </CellSub>
                      {sel.includes(m.id) && (
                        <ShareIntroducerTick first={(detail?.referrer?.name || "").split(" ")[0]} on={share.includes(m.id)}
                                             onToggle={() => setShare((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])}
                                             testId={`route-desk-share-introducer-${m.id}`} />
                      )}
                      {(m.capacity?.paused || m.capacity?.at_capacity) && (
                        <CellSub style={{ marginTop: 4 }}>
                          <span data-testid={`route-desk-capacity-${m.id}`}>
                            {m.capacity.paused ? "Paused — they'll see it when they're back"
                              : `Over their cap (${m.capacity.used}/${m.capacity.cap_count})`}
                          </span>
                        </CellSub>
                      )}
                    </div>
                    {m.fit && (
                      <span className="st st-off" data-testid={`route-desk-fit-${m.id}`}>
                        <i className="sq" />{m.fit.score}%
                      </span>
                    )}
                  </label>
                ))}
              </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
              <Btn onClick={() => { setPicked(null); setDetail(null); setSel([]); }} testId="route-case-back">
                Pick a different case
              </Btn>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                {sel.length > 0 && <Tag>{sel.length} picked</Tag>}
                <Btn kind="primary" onClick={send} disabled={sending || sel.length === 0} testId="route-case-send">
                  {sending ? "Sending…" : `Send to ${sel.length || 0} desk${sel.length === 1 ? "" : "s"}`}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
